/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

// The words of a Word file (.docx), read in the browser so a paper form kept
// as Word can be drafted into a form like a PDF or a photo can. A .docx is a
// zip; its text is in word/document.xml. Only that one entry is unpacked, with
// the browser's own inflater, and only so far: a zip that claims or grows past
// MAX_XML is refused, so a crafted file cannot fill the tab's memory.

const W = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
const DOCUMENT = 'word/document.xml';
/** Far beyond the XML of any form on paper; a Word file of a few hundred pages. */
export const MAX_XML = 20 * 1024 * 1024;

export const isDocx = (name: string): boolean => /\.docx$/i.test(name);

/** The text of a .docx: paragraphs on lines of their own, a table's cells joined with " | " row by row. Empty when there is none. */
export async function docxText(file: Blob): Promise<string> {
  const xml = await documentXml(new Uint8Array(await file.arrayBuffer()));
  const document = new DOMParser().parseFromString(xml, 'application/xml');
  if (document.getElementsByTagName('parsererror').length) {
    throw new Error('word/document.xml is not XML');
  }
  const body = document.getElementsByTagNameNS(W, 'body')[0];
  if (!body) {
    return '';
  }
  const lines: string[] = [];
  blockText(body, lines);
  return lines
    .map((line) => line.replace(/[ \t\u00a0]+/g, ' ').trim())
    .filter((line, i, all) => line || (i > 0 && all[i - 1]))
    .join('\n')
    .trim();
}

/** Paragraphs and tables in order; anything that wraps them (a content control, a custom XML block) is looked into. */
function blockText(parent: Element, lines: string[]): void {
  for (const child of Array.from(parent.children)) {
    if (child.namespaceURI === W && child.localName === 'p') {
      lines.push(...runText(child).split('\n'));
    } else if (child.namespaceURI === W && child.localName === 'tbl') {
      for (const row of Array.from(child.children).filter((c) => c.localName === 'tr')) {
        const cells = Array.from(row.getElementsByTagNameNS(W, 'tc'))
          .filter((cell) => cell.parentElement === row || cell.parentElement?.parentElement === row)
          .map((cell) => cellText(cell));
        lines.push(cells.join(' | '));
      }
      lines.push('');
    } else if (child.localName !== 'sectPr') {
      blockText(child, lines);
    }
  }
}

/** One cell on one line: its paragraphs, nested tables included, run together. */
function cellText(cell: Element): string {
  const lines: string[] = [];
  blockText(cell, lines);
  return lines
    .map((l) => l.trim())
    .filter(Boolean)
    .join(' ');
}

/** The runs of one paragraph: text, tabs and line breaks; deleted text in tracked changes is left out. */
function runText(paragraph: Element): string {
  let text = '';
  const walk = (node: Element): void => {
    for (const child of Array.from(node.children)) {
      if (child.namespaceURI !== W) {
        walk(child);
        continue;
      }
      switch (child.localName) {
        case 't':
          text += child.textContent ?? '';
          break;
        case 'tab':
          text += '\t';
          break;
        case 'br':
        case 'cr':
          text += '\n';
          break;
        case 'del':
        case 'delText':
        case 'instrText':
        case 'rPr':
        case 'pPr':
          break;
        default:
          walk(child);
      }
    }
  };
  walk(paragraph);
  return text;
}

/** Finds word/document.xml through the zip's central directory and unpacks it. */
async function documentXml(zip: Uint8Array): Promise<string> {
  const view = new DataView(zip.buffer, zip.byteOffset, zip.byteLength);
  const end = endOfDirectory(view);
  const count = view.getUint16(end + 10, true);
  let at = view.getUint32(end + 16, true);

  for (let i = 0; i < count; i++) {
    if (at + 46 > zip.length || view.getUint32(at, true) !== 0x02014b50) {
      break;
    }
    const method = view.getUint16(at + 10, true);
    const packed = view.getUint32(at + 20, true);
    const size = view.getUint32(at + 24, true);
    const nameLength = view.getUint16(at + 28, true);
    const skip = nameLength + view.getUint16(at + 30, true) + view.getUint16(at + 32, true);
    const local = view.getUint32(at + 42, true);
    const name = new TextDecoder().decode(zip.subarray(at + 46, at + 46 + nameLength));
    at += 46 + skip;
    if (name !== DOCUMENT) {
      continue;
    }

    if (size > MAX_XML || local + 30 > zip.length || view.getUint32(local, true) !== 0x04034b50) {
      throw new Error('word/document.xml cannot be read');
    }
    const start = local + 30 + view.getUint16(local + 26, true) + view.getUint16(local + 28, true);
    const data = zip.subarray(start, start + packed);
    if (method === 0) {
      return new TextDecoder().decode(data);
    }
    if (method === 8) {
      return new TextDecoder().decode(await inflate(data));
    }
    throw new Error(`word/document.xml is packed with method ${method}`);
  }
  throw new Error('not a Word file: word/document.xml is missing');
}

/** The "end of central directory" record: the last thing in a zip, before a comment of up to 64 KB. */
function endOfDirectory(view: DataView): number {
  for (let at = view.byteLength - 22; at >= Math.max(0, view.byteLength - 22 - 0xffff); at--) {
    if (view.getUint32(at, true) === 0x06054b50) {
      return at;
    }
  }
  throw new Error('not a zip file');
}

async function inflate(data: Uint8Array): Promise<Uint8Array> {
  const reader = new Blob([data]).stream().pipeThrough(new DecompressionStream('deflate-raw')).getReader();
  const parts: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    const { value, done } = await reader.read();
    if (done) {
      break;
    }
    total += value.length;
    if (total > MAX_XML) {
      await reader.cancel();
      throw new Error('word/document.xml is too large');
    }
    parts.push(value);
  }
  const out = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    out.set(part, offset);
    offset += part.length;
  }
  return out;
}
